import React from "react";
import { motion } from "framer-motion";
import Tilt from "react-parallax-tilt";
import { ArrowRight, ChevronDown } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { Particles } from "./Particles";
import { Counter } from "./Counter";
import { BRAND, STATS, waOrder } from "../../data/zingly";

export const Hero = () => {
  const scrollMenu = () => {
    document.getElementById("menu")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100vh] pt-28 pb-20 overflow-hidden bg-gradient-to-b from-[#FFF8F1] via-[#FFF1E0] to-[#FFF8F1]"
    >
      <Particles count={16} />

      {/* Decorative blobs */}
      <div className="absolute -top-20 -right-20 w-[480px] h-[480px] rounded-full bg-[#F58220]/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-20 w-[420px] h-[420px] rounded-full bg-[#2E7D32]/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* Left content */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10"
        >
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.1 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/70 backdrop-blur border border-orange-100 shadow-sm"
            data-testid="hero-tagline"
          >
            <span className="w-2 h-2 rounded-full bg-[#2E7D32] animate-pulse" />
            <span className="text-xs md:text-sm font-medium text-[#1E1E1E] tracking-wide">
              Fresh Batter • Homemade • Pure Veg
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mt-6 font-serif text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] text-[#1E1E1E]"
            data-testid="hero-heading"
          >
            Experience{" "}
            <span className="relative inline-block">
              <span className="text-[#F58220]">Authentic</span>
              <span className="absolute left-0 right-0 -bottom-2 h-2 bg-[#F58220]/20 rounded-full" />
            </span>
            <br />
            South Indian Dosa
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.8 }}
            className="mt-6 text-base md:text-lg text-[#555555] max-w-xl leading-relaxed"
          >
            Freshly prepared everyday using premium ingredients. Crispy Plain Dosa, Masala Dosa and
            Soft Idli — delivered hot to your doorstep in Beed.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button
              onClick={scrollMenu}
              data-testid="hero-view-menu-btn"
              className="btn-3d inline-flex items-center gap-2 px-7 py-4 rounded-full bg-gradient-to-r from-[#F58220] to-[#E16608] text-white font-semibold text-[15px]"
            >
              View Menu <ArrowRight className="w-4 h-4" />
            </button>
            <a
              href={waOrder()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="btn-3d-outline inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-[#2E7D32] font-semibold text-[15px] border-2 border-[#2E7D32]"
            >
              <FaWhatsapp className="w-5 h-5" /> Order on WhatsApp
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.8 }}
            className="mt-14 grid grid-cols-3 gap-4 max-w-lg"
            data-testid="hero-stats"
          >
            {STATS.map((s, i) => (
              <div
                key={s.label}
                className="text-center md:text-left"
                data-testid={`stat-${i}`}
              >
                <div className="font-serif text-4xl md:text-5xl font-extrabold text-[#1E1E1E]">
                  <Counter to={s.value} suffix={s.suffix} />
                </div>
                <div className="mt-1 text-xs md:text-sm uppercase tracking-wider text-[#555555] font-medium">
                  {s.label}
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Right image with 3D tilt */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.3 }}
          className="relative z-10"
          data-testid="hero-image-wrapper"
        >
          <Tilt
            tiltMaxAngleX={12}
            tiltMaxAngleY={12}
            glareEnable
            glareMaxOpacity={0.15}
            glareColor="#ffffff"
            glarePosition="all"
            scale={1.02}
            transitionSpeed={1200}
            className="rounded-[2rem]"
          >
            <motion.div
              animate={{ y: [0, -14, 0] }}
              transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }}
              className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_80px_-20px_rgba(245,130,32,0.5)] border-[10px] border-white"
            >
              <img
                src="https://images.unsplash.com/photo-1743615467204-8fdaa85ff2db?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwzfHxjcmlzcHklMjBkb3NhfGVufDB8fHx8MTc4MjU2OTE2OHww&ixlib=rb-4.1.0&q=85"
                alt="Crispy authentic dosa"
                className="w-full h-[460px] md:h-[560px] object-cover"
                data-testid="hero-image"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
            </motion.div>
          </Tilt>

          {/* Floating chips */}
          <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ repeat: Infinity, duration: 5, ease: "easeInOut", delay: 0.5 }}
            className="absolute -left-2 md:-left-10 top-10 bg-white shadow-xl rounded-2xl px-4 py-3 border border-orange-100 flex items-center gap-3"
          >
            <span className="w-10 h-10 rounded-full bg-[#F58220]/15 flex items-center justify-center text-xl">🌶️</span>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#555555]">Today</div>
              <div className="font-semibold text-[#1E1E1E] text-sm">Fresh Batter Ready</div>
            </div>
          </motion.div>

          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ repeat: Infinity, duration: 5.5, ease: "easeInOut", delay: 0.2 }}
            className="absolute -right-2 md:-right-6 bottom-12 bg-white shadow-xl rounded-2xl px-4 py-3 border border-green-100 flex items-center gap-3"
          >
            <span className="w-10 h-10 rounded-full bg-[#2E7D32]/15 flex items-center justify-center text-xl">⭐</span>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#555555]">5.0 Rating</div>
              <div className="font-semibold text-[#1E1E1E] text-sm">Loved by Beed</div>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.button
        onClick={scrollMenu}
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[#1E1E1E]/60 hover:text-[#F58220]"
        aria-label="Scroll to menu"
        data-testid="hero-scroll-indicator"
      >
        <ChevronDown className="w-7 h-7" />
      </motion.button>
    </section>
  );
};
